import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useFeatures } from '../contexts/FeatureContext';

/**
 * Wrapper that hides a route behind a feature flag.
 *
 * Behaviour:
 *  - While feature config is loading, renders nothing (prevents flash).
 *  - If the feature is enabled for the current mode, renders children.
 *  - If the feature is disabled, fires a toast and redirects the user to
 *    `/:workspaceId/pipeline` (falls back to `/` when there's no workspace,
 *    which is the case for super-admin routes).
 */
export default function FeatureGate({ featureId, children }) {
  const { isFeatureEnabled, isLoaded } = useFeatures();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const notified = useRef(false);

  const allowed = isFeatureEnabled(featureId);

  useEffect(() => {
    if (!isLoaded || allowed) return;
    if (notified.current) return;
    notified.current = true;
    toast.error('This feature is not available in the current environment.', {
      description: 'Redirecting you back to the pipeline.',
    });
    const fallback = workspaceId ? `/${workspaceId}/pipeline` : '/';
    navigate(fallback, { replace: true });
  }, [isLoaded, allowed, navigate, workspaceId]);

  if (!isLoaded) return null;
  if (!allowed) return null;
  return children;
}
